import { useEffect, useState } from "react";
import { BellOff, BellRing } from "lucide-react";
import type { Participant } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  getUploadAlertBadgeLabel,
  getUploadAlertDescription,
  getUploadAlertKey,
} from "@/lib/upload-alerts";

const NOTIFIED_ALERT_KEYS_STORAGE_KEY = "edgecare-notified-data-gap-alerts";

type WebNotificationPermission = NotificationPermission | "unsupported";

let notificationServiceWorkerPromise: Promise<ServiceWorkerRegistration> | null =
  null;

function getPermission(): WebNotificationPermission {
  return "Notification" in window ? Notification.permission : "unsupported";
}

function readNotifiedAlertKeys(value: string): Set<string> {
  try {
    const parsed = JSON.parse(value);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function getParticipantLabel(participant: Participant): string {
  return participant.username || participant.user_id?.slice(0, 8) || "unknown";
}

function getNotificationServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  if (!notificationServiceWorkerPromise) {
    notificationServiceWorkerPromise = navigator.serviceWorker
      .register("/notification-sw.js")
      .then(() => navigator.serviceWorker.ready)
      .catch((error) => {
        notificationServiceWorkerPromise = null;
        throw error;
      });
  }
  return notificationServiceWorkerPromise;
}

async function showNotification(participants: Participant[]) {
  const title =
    participants.length === 1
      ? "EdgeCare data collection alert"
      : `EdgeCare: ${participants.length} data collection alerts`;
  const body =
    participants.length === 1
      ? `${getParticipantLabel(participants[0])}: ${getUploadAlertDescription(participants[0])}`
      : `${participants
          .slice(0, 3)
          .map(
            (participant) =>
              `${getParticipantLabel(participant)} (${getUploadAlertBadgeLabel(participant)})`,
          )
          .join(", ")}${participants.length > 3 ? ` and ${participants.length - 3} more` : ""}`;
  const options: NotificationOptions = {
    body,
    icon: "/favicon.svg",
    tag: "edgecare-data-collection-alerts",
  };

  const registration = getNotificationServiceWorker();
  if (registration) {
    const readyRegistration = await registration;
    await readyRegistration.showNotification(title, {
      ...options,
      data: { url: "/" },
    });
    return;
  }

  const notification = new Notification(title, options);
  notification.onclick = () => {
    window.focus();
    window.location.assign("/");
    notification.close();
  };
}

export function WebAlertNotifications({
  participants,
  dismissedAlertSignature,
}: {
  participants: Participant[];
  dismissedAlertSignature: string;
}) {
  const [permission, setPermission] =
    useState<WebNotificationPermission>(getPermission);

  useEffect(() => {
    function refreshPermission() {
      setPermission(getPermission());
    }

    window.addEventListener("focus", refreshPermission);
    return () => window.removeEventListener("focus", refreshPermission);
  }, []);

  useEffect(() => {
    if (permission !== "granted") return;

    const currentKeys = participants.map(getUploadAlertKey);
    const storedKeys = localStorage.getItem(NOTIFIED_ALERT_KEYS_STORAGE_KEY);

    if (storedKeys === null) {
      localStorage.setItem(
        NOTIFIED_ALERT_KEYS_STORAGE_KEY,
        JSON.stringify(currentKeys),
      );
      return;
    }

    const previousKeys = readNotifiedAlertKeys(storedKeys);
    const dismissedKeys = new Set(
      dismissedAlertSignature.split("|").filter(Boolean),
    );
    const newAlerts = participants.filter(
      (participant) => {
        const key = getUploadAlertKey(participant);
        return !previousKeys.has(key) && !dismissedKeys.has(key);
      },
    );

    localStorage.setItem(
      NOTIFIED_ALERT_KEYS_STORAGE_KEY,
      JSON.stringify(currentKeys),
    );

    if (newAlerts.length > 0) {
      void showNotification(newAlerts).catch((error) => {
        console.error("[WebAlerts] Failed to show notification", error);
      });
    }
  }, [dismissedAlertSignature, participants, permission]);

  async function enableNotifications() {
    if (!("Notification" in window)) return;
    try {
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);
    } catch {
      setPermission(getPermission());
    }
  }

  if (permission === "unsupported") {
    return (
      <Button variant="outline" size="sm" disabled>
        <BellOff />
        Web alerts unavailable
      </Button>
    );
  }

  if (permission === "granted") {
    return (
      <Button variant="outline" size="sm" disabled>
        <BellRing />
        Web alerts on
      </Button>
    );
  }

  if (permission === "denied") {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        title="Allow notifications for this site in your browser settings"
      >
        <BellOff />
        Web alerts blocked
      </Button>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={enableNotifications}>
      <BellRing />
      Enable web alerts
    </Button>
  );
}
